/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from "react";
import { Menu, X, ArrowRight, ShieldCheck, User, LogIn, UserPlus, Download, Smartphone } from "lucide-react";
import { useSiteConfig } from "../../context/SiteConfigContext";
import { DEFAULT_LOGO_URL } from "../../utils/brandLogo";
const logoImg = DEFAULT_LOGO_URL;

interface LandingHeaderProps {
  onLogin: () => void;
  onRegister: () => void;
  onGetStarted: () => void;
  onAdminLogin?: () => void;
  onNavigateSection: (sectionId: string) => void;
  onOpenApiDocs?: () => void;
}

export const LandingHeader: React.FC<LandingHeaderProps> = ({
  onLogin,
  onRegister,
  onGetStarted,
  onAdminLogin,
  onNavigateSection,
  onOpenApiDocs,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const { config, logoUrl: configuredLogoUrl, siteName } = useSiteConfig();
  const activeLogo = config.branding?.logoUrl || config.branding?.lightLogoUrl || configuredLogoUrl || logoImg;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (sectionId: string) => {
    if (sectionId === "api-section" || sectionId === "api") {
      setMobileMenuOpen(false);
      if (onOpenApiDocs) {
        onOpenApiDocs();
        return;
      }
    }
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
    onNavigateSection(sectionId);
  };

  return (
    <header
      id="site-header"
      role="banner"
      className={`sticky top-0 z-40 w-full min-h-[56px] sm:min-h-[64px] transition-colors duration-200 border-b mobile-gpu-layer ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-xs border-[#E5E7EB]"
          : "bg-white border-[#E5E7EB]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16 py-1">
          
          {/* Left: SmartLink Logo */}
          <button
            id="header-logo-container"
            type="button"
            onClick={() => handleNavClick("hero-section")}
            className="flex items-center cursor-pointer group bg-transparent border-none p-0 text-left shrink-0 min-h-[48px]"
            aria-label="Smart Link NG Home"
          >
            <img
              src={activeLogo}
              alt={`${siteName || "Smart Link NG"} - Official Identity Verification & Fintech Portal`}
              width={180}
              height={48}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="h-10 sm:h-12 md:h-13 w-auto max-w-[170px] sm:max-w-[210px] object-contain shrink-0"
              referrerPolicy="no-referrer"
            />
          </button>

          {/* Center: Desktop Navigation Links */}
          <nav id="header-desktop-nav" aria-label="Primary Desktop Navigation" className="hidden md:flex items-center gap-1 lg:gap-1.5">
            {[
              { id: "hero-section", label: "Home" },
              { id: "services-section", label: "Services" },
              { id: "pricing-section", label: "Pricing" },
              { id: "api-section", label: "API" },
              { id: "about-section", label: "About" },
              { id: "contact-section", label: "Contact" },
            ].map((item) => (
              <button
                key={item.id}
                id={`nav-link-${item.id}`}
                type="button"
                onClick={() => handleNavClick(item.id)}
                className={`min-h-[40px] px-3.5 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  activeSection === item.id
                    ? "text-[#0F2D5C] bg-[#F5F7FA] font-bold"
                    : "text-[#1E293B] hover:text-[#0F2D5C] hover:bg-[#F5F7FA]"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right: Actions (Login, Register, Get Started) */}
          <div id="header-right-actions" className="hidden lg:flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("open_pwa_install_modal"))}
              className="min-h-[40px] px-3 py-2 text-xs font-bold text-[#0F2D5C] bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 shadow-2xs"
              title="Install SmartLink App on your Phone/PC"
              aria-label="Install SmartLink Application"
            >
              <Smartphone className="h-3.5 w-3.5 text-amber-700" aria-hidden="true" />
              <span>Install App</span>
            </button>

            <button
              id="header-btn-login"
              type="button"
              onClick={onLogin}
              className="min-h-[40px] px-3.5 py-2 text-xs font-bold text-white bg-[#111827] hover:bg-[#0F2D5C] focus:ring-2 focus:ring-offset-2 focus:ring-[#0F2D5C] rounded-lg transition-all cursor-pointer flex items-center gap-1.5"
            >
              <LogIn className="h-3.5 w-3.5 text-white" aria-hidden="true" />
              <span>Login</span>
            </button>

            <button
              id="header-btn-register"
              type="button"
              onClick={onRegister}
              className="min-h-[40px] px-3.5 py-2 text-xs font-bold text-white bg-[#111827] hover:bg-[#0F2D5C] focus:ring-2 focus:ring-offset-2 focus:ring-[#0F2D5C] rounded-lg transition-all cursor-pointer flex items-center gap-1.5"
            >
              <UserPlus className="h-3.5 w-3.5 text-white" aria-hidden="true" />
              <span>Register</span>
            </button>

            <button
              id="header-btn-get-started"
              type="button"
              onClick={onGetStarted}
              className="min-h-[40px] px-4 py-2 bg-[#0F2D5C] hover:bg-[#17407E] text-white font-bold rounded-lg text-xs shadow-xs focus:ring-2 focus:ring-offset-2 focus:ring-[#0F2D5C] transition-all active:scale-98 cursor-pointer flex items-center gap-1.5"
            >
              <span>Get Started</span>
              <ArrowRight className="h-3.5 w-3.5 text-white" aria-hidden="true" />
            </button>
          </div>

          {/* Mobile Hamburger Toggle with compliant 48x48px touch target */}
          <div className="flex items-center gap-1.5 lg:hidden">
            <button
              id="header-mobile-toggle"
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="min-w-[48px] min-h-[48px] flex items-center justify-center p-2 rounded-lg text-[#111827] hover:bg-[#F5F7FA] active:bg-[#E5E7EB] transition-colors cursor-pointer touch-manipulation focus:outline-none focus:ring-2 focus:ring-[#0F2D5C]"
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-drawer-overlay"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop Overlay */}
          <div
            className="lg:hidden fixed inset-0 top-14 sm:top-16 bg-slate-900/40 backdrop-blur-xs z-40 transition-opacity"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Side Drawer Panel */}
          <div
            id="mobile-drawer-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile Navigation Menu"
            className="lg:hidden fixed top-14 sm:top-16 right-0 w-3/4 sm:w-1/2 max-w-xs h-[calc(100vh-56px)] sm:h-[calc(100vh-64px)] bg-white border-l border-slate-200 shadow-2xl p-4 transition-all z-50 overflow-y-auto"
          >
            <div className="space-y-4">
              <div className="space-y-1">
                {[
                  { id: "hero-section", label: "Home" },
                  { id: "services-section", label: "Services" },
                  { id: "pricing-section", label: "Pricing" },
                  { id: "api-section", label: "API" },
                  { id: "about-section", label: "About" },
                  { id: "contact-section", label: "Contact" },
                ].map((item) => (
                  <button
                    key={item.id}
                    id={`mobile-nav-link-${item.id}`}
                    type="button"
                    onClick={() => handleNavClick(item.id)}
                    className="w-full text-left px-3.5 py-3 min-h-[44px] text-sm font-semibold transition-all rounded-xl cursor-pointer flex items-center justify-between touch-manipulation text-slate-800 hover:bg-slate-50 active:scale-[0.99]"
                  >
                    <span className="text-sm font-semibold">{item.label}</span>
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default LandingHeader;
